<template>
  <div class="bookmark-list">
    <ui-menu
      v-model="menu"
      class="bookmark-list__activator-wrapper"
      :content-class="menuContentClass"
      :close-on-content-click="false"
      :location="location"
      :offset="offset"
    >
      <template #activator="{ props: activatorProps }">
        <slot name="activator" :open="menu" :props="activatorProps">
          <button
            type="button"
            v-bind="activatorProps"
            class="navbar__action-button navbar__profile-tab bookmark-list__profile-tab"
          >
            <ui-icon :color="$colors.white" class="bookmark-list__profile-tab-icon" icon="fas fa-star"></ui-icon>
            <ui-chevron
              class="ml-4 chevron"
              :color="$colors.white"
              :open="menu"
            ></ui-chevron>
          </button>
        </slot>
      </template>

      <template #menu>
        <div class="bookmark-list__content">
          <div v-if="bookmarks.length" class="bookmark-list__search-block">
            <ui-icon size="15" :color="$colors.gray40" icon="fas fa-search"></ui-icon>
            <v-text-field
              v-model="search"
              :placeholder="t('Search')"
            ></v-text-field>
          </div>

          <v-list class="bookmark-list__bookmark-list" v-if="bookmarksFiltered.length">
            <v-list-item
              v-for="item in bookmarksFiltered"
              :key="item.index"
              class="bookmark-list__bookmark-list-item"
              @click="handleOpenBookmark(item)"
            >
              <v-list-item-title class="bookmark-list__bookmark-item-wrapper">
                <div class="bookmark-list__bookmark-item">
                  <div class="bookmark-list__item-title">{{ item.bookmarkTitle }}</div>
                  <div class="bookmark-list__item-actions">
                    <ui-icon
                      class="bookmark-list__icon--delete"
                      @click.stop="openDeleteDialog(item.index)"
                      size="13"
                      icon="fas fa-trash-alt"
                    ></ui-icon>
                  </div>
                </div>
              </v-list-item-title>
            </v-list-item>
          </v-list>

          <div v-else class="bookmark-list__no-items">
            <template v-if="bookmarks.length">
              {{ t('No bookmarks matched your search') }}
            </template>
            <template v-else>
              <div
                v-html="
                  t(
                    `Quickly access your saved reports from this section. <br>To bookmark a report, click the arrow icon next to it. <br><a target='_blank' href='{0}'>Learn more</a> in this guide.`,
                    [`https://docs.bemob.com/${locale}/bookmarks`],
                  )
                "
              ></div>
            </template>
          </div>
        </div>
      </template>
    </ui-menu>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useBookmarkList, useColors, useI18n } from '@/composables';

const props = withDefaults(
  defineProps<{
    contentClass?: string;
    location?: string;
    offset?: [number, number];
  }>(),
  {
    contentClass: '',
    location: 'bottom start',
    offset: () => [0, 8],
  },
);

const { t, locale } = useI18n();
const $colors = useColors();

const menu = ref(false);
const search = ref('');

const { bookmarks, openDeleteDialog, handleOpenBookmark } = useBookmarkList({
  onClose: () => {
    menu.value = false;
  },
});

const menuContentClass = computed(() =>
  ['bookmark-list__menu', props.contentClass].filter(Boolean).join(' '),
);

const bookmarksFiltered = computed(() => {
  const searchValue = search.value.toLowerCase();
  return bookmarks.value.filter((item) => item.titleLowerCase.includes(searchValue));
});
</script>

<style lang="scss">
.bookmark-list__activator-wrapper {
  height: 100%;
  width: 100%;
}

.v-overlay__content.bookmark-list__menu {
  min-width: 315px !important;
  max-width: 322px !important;
  background-color: var(--color-white);
  border-radius: 8px !important;
}

.bookmark-list__profile-tab:hover {
  .bookmark-list__profile-tab-icon {
    color: var(--color-white) !important;
  }
}

.bookmark-list__profile-tab .chevron {
  margin-top: 3px;
}

.bookmark-list__content {
  max-width: 322px;
}

.bookmark-list__bookmark-list {
  max-height: 368px;
  overflow-y: auto;

  [role='listitem'] + [role='listitem'] {
    border-top: 1px solid var(--color-gray10);
  }

  .v-list-item {
    min-height: 34px;

    &:hover {
      background-color: var(--color-green10);
    }
  }
}

.bookmark-list__bookmark-item-wrapper {
  cursor: pointer;
}

.bookmark-list__bookmark-item {
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  color: var(--color-graphite);
}

.bookmark-list__no-items {
  font-size: 13px;
  padding: 16px;
}

.bookmark-list__item-title {
  text-overflow: ellipsis;
  overflow-x: hidden;
  margin-right: 5px;
  font-size: 13px;
}

.bookmark-list__item-actions {
  display: flex;
  align-items: center;
}

.bookmark-list__icon--delete:hover {
  color: var(--color-red) !important;
}

.bookmark-list__icon--edit:hover {
  color: var(--color-green) !important;
}

.bookmark-list__search-block {
  position: relative;
  display: flex;

  .ui-icon {
    z-index: 3;
    position: absolute;
    top: calc(50% - 7px);
    left: 16px;
  }

  .v-input {
    padding: 0 !important;
  }

  .v-field__input {
    margin-bottom: 0 !important;
  }

  .v-input__control {
    height: 43px;
  }

  input {
    padding-left: 45px !important;
    margin: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    min-height: 43px;
    height: 43px;
    border-bottom: 1px solid var(--color-gray10) !important;

    &::placeholder {
      opacity: 0.4;
      font-size: 15px;
    }
  }
}
</style>
